import {
  AlertModal,
  AlertModalType,
  Button,
  Flex,
  FlexColumn,
  Input,
  isUserWithRoles,
  RadioInput,
  SecondaryButton,
  SelectInput,
  TextareaInput,
  useCombinedUser,
  UserRoles,
} from '@equifinance/shared';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { uniq, cloneDeep, set } from 'lodash';
import { useApplicationsContext, WithdrawnReasons } from '../../../contexts/applications';
import { WithdrawReasonType } from '../../../api/useAccountApi';
import { ErrorList } from '../../CasesTable/CasesTable';

const RejectModalForm = styled(FlexColumn)`${({ theme }) => `
  gap: ${theme.padding[6]};
`}`;

const RejectModalContainer = styled(FlexColumn)`${({ theme }) => `
  gap: ${theme.padding[8]};
`}`;

const RejectModalButtonContainer = styled(Flex)`${({ theme }) => `
  padding-top: ${theme.padding[3]};
  gap: ${theme.padding[2]};
  justify-content: end;
`}`;

const defaultReason = { reason: '', otherDescription: '' };
const defaultWithdrawError: {
  reason: string[],
  otherDescription: string[],
  otherErrors: string[]
} = {
  reason: [],
  otherDescription: [],
  otherErrors: [],
};

function RejectModalContent({
  applicationId,
  isUnderwriter,
  close,
}:{ applicationId: string, isUnderwriter: boolean, close: () => void }) {
  const [status, setStatus] = useState<'reject' | 'withdraw' | null>(isUnderwriter ? null : 'withdraw');
  const [reasons, setReasons] = useState<{
    key: string,
    value: string
  }[]>([]);
  const [reasonType, setReasonType] = useState<Required<WithdrawReasonType>>(defaultReason);
  const {
    getRejectionReasons, getWithdrawalReasons, rejectApplication, withdrawApplication,
  } = useApplicationsContext();
  const [withdrawError, setWithdrawError] = useState(defaultWithdrawError);

  const isSubmitDisabled = !reasonType.reason || (reasonType.reason === WithdrawnReasons.Other && !reasonType.otherDescription);

  const onClick = async () => {
    close();
    setWithdrawError(defaultWithdrawError);
    const reasonPayload = reasonType.reason !== WithdrawnReasons.Other
      ? { reason: reasonType.reason }
      : { ...reasonType };

    const response = await (status === 'reject'
      ? rejectApplication(applicationId, reasonPayload)
      : withdrawApplication(applicationId, reasonPayload));

    if (typeof response === 'string') {
      setWithdrawError((prev) => ({
        ...prev,
        otherErrors: [response],
      }));
      return;
    }

    if (response.errors) {
      const responseErrors = Object.entries(response.errors).reduce((prev, [key, errors]) => {
        if (['reason', 'otherDescription'].includes(key)) {
          set(prev, key, errors);
        } else {
          prev.otherErrors = uniq([...prev.otherErrors, ...errors]);
        }
        return prev;
      }, cloneDeep(defaultWithdrawError));
      setWithdrawError(responseErrors);
    }
  };

  useEffect(() => {
    (async () => {
      setReasons(await (status === 'reject' ? getRejectionReasons() : getWithdrawalReasons()));
    })();
    setReasonType(defaultReason);
  }, [status]);

  return (
    <RejectModalContainer>
      <RejectModalForm>
        {isUnderwriter && (
          <Input
            label="How do you wish to proceed:"
            id="application-reject-type"
            required
          >
            <FlexColumn>
              <RadioInput
                id="application-reject-type-reject"
                label="Reject application"
                value={status === 'reject'}
                onChange={() => setStatus('reject')}
              />
              <RadioInput
                id="application-reject-type-withdraw"
                label="Withdraw application"
                value={status === 'withdraw'}
                onChange={() => setStatus('withdraw')}
              />
            </FlexColumn>
          </Input>
        )}
        {status && (
        <SelectInput
          id="application-reject-withdraw-reason"
          placeholder="Select"
          label={`Reason for ${(status === 'reject') ? 'rejection' : 'withdrawal'}`}
          value={reasonType.reason}
          onChange={(value) => setReasonType((prev) => ({ ...prev, reason: value }))}
          options={reasons.reduce((prev, current) => ({
            ...prev,
            [current.key]: current.value,
          }), {})}
          errors={withdrawError.reason}
          touchOverride
          required
        />
        )}
        {reasonType.reason === WithdrawnReasons.Other && (
          <TextareaInput
            id="withdraw-application-other-reason"
            value={reasonType.otherDescription || ''}
            onChange={(value) => setReasonType((prev) => ({ ...prev, otherDescription: value }))}
            label="Please provide details"
            rows={1}
            errors={withdrawError.otherDescription}
            touchOverride
            required
          />
        )}
        {!!withdrawError.otherErrors.length && (
          <ErrorList isBulletShown={withdrawError.otherErrors.length > 1}>
            {withdrawError.otherErrors.map((errorMessage) => (
              <li key={errorMessage}>{errorMessage}</li>
            ))}
          </ErrorList>
        )}
      </RejectModalForm>
      <RejectModalButtonContainer>
        <SecondaryButton small onClick={close}>Cancel</SecondaryButton>
        <Button disabled={isSubmitDisabled} small onClick={onClick}>Confirm</Button>
      </RejectModalButtonContainer>
    </RejectModalContainer>
  );
}

export type RejectModalProps = Omit<AlertModalType, 'content'> & {
  applicationId: string;
  onClose: () => void;
}

export default function RejectModal({
  applicationId,
  onClose,
  ...props
}: RejectModalProps) {
  const { user } = useCombinedUser();
  const isUnderwriter = isUserWithRoles([UserRoles.UNDERWRITER], user);
  return (
    <AlertModal
      {...props}
      title={isUnderwriter ? 'Reject / Withdraw the application' : 'Withdraw the application'}
      content={(
        <RejectModalContent
          applicationId={applicationId}
          isUnderwriter={isUnderwriter}
          close={onClose}
        />
      )}
      onClose={onClose}
      showButtons={false}
    />
  );
}
